import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ArrowLeft, User, Camera, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES, ROLE_LABELS, ROLE_ICON_COMPONENTS } from '../../lib/rolePermissions';
import { validateAvatarFile, uploadAvatarImage, removeAvatarImage } from '../../lib/avatarService';
import { normalizeInstagramUsername, checkInstagramUsernameAvailable } from '../../lib/artistWebService';
import { supabase } from '../../lib/supabase';
import logo from '../../assets/SenritsuXwordmarkwhite.png';
import '../../styles/Auth.css';

const AVAILABLE_ROLES = [
  ROLES.SINGER,
  ROLES.RAPPER,
  ROLES.PRODUCER,
  ROLES.VIDEO_EDITOR,
  ROLES.GRAPHIC_DESIGNER,
];

export default function EditProfilePage() {
  const { user, profile, roles, updateProfile, updateRoles, completeOnboarding, loading, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const searchParams = new URLSearchParams(location.search);
  const isFirstTime = searchParams.get('firstTime') === 'true' || (profile && profile.onboarding_completed === false);

  const [form, setForm] = useState({
    name: '',
    legal_name: '',
    stage_name: '',
    genre: '',
    secondary_genre: '',
    city: '',
    phone: '',
    artist_web_about: '',
    spotify_link: '',
    apple_music_link: '',
    youtube_music_link: '',
    instagram: '',
    youtube: '',
    upi_id: '',
  });

  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarTimestamp, setAvatarTimestamp] = useState(Date.now());
  const [avatarError, setAvatarError] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removingAvatar, setRemovingAvatar] = useState(false);

  const [selectedRoles, setSelectedRoles] = useState([]);
  const [primaryRole, setPrimaryRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Populate form and roles when profile or user loads
  useEffect(() => {
    if (profile) {
      setForm({
        name: profile.name || '',
        legal_name: profile.legal_name || '',
        stage_name: profile.stage_name || '',
        genre: profile.genre || '',
        secondary_genre: profile.secondary_genre || '',
        city: profile.city || '',
        phone: profile.phone || '',
        artist_web_about: profile.artist_web_about || '',
        spotify_link: profile.spotify_link || '',
        apple_music_link: profile.apple_music_link || '',
        youtube_music_link: profile.youtube_music_link || '',
        instagram: profile.instagram || '',
        youtube: profile.youtube || '',
        upi_id: profile.upi_id || '',
      });
      setAvatarUrl(profile.avatar_url || '');
      setAvatarError(false);
    } else if (user) {
      const meta = user.user_metadata || {};
      const initialAvatar = meta.avatar_url || meta.picture || (Array.isArray(meta.images) && meta.images[0]?.url) || '';
      setForm((prev) => ({
        ...prev,
        name: meta.full_name || meta.name || prev.name || '',
        legal_name: meta.legal_name || prev.legal_name || '',
        stage_name: meta.stage_name || prev.stage_name || '',
        phone: meta.phone || prev.phone || '',
        city: meta.city || prev.city || '',
      }));
      if (initialAvatar) {
        setAvatarUrl(initialAvatar);
        setAvatarError(false);
      }
    }

    if (roles && roles.length > 0) {
      setSelectedRoles(roles);
      setPrimaryRole((prev) => (prev && roles.includes(prev) ? prev : roles[0]));
    } else if (user?.user_metadata?.primary_role) {
      setPrimaryRole(user.user_metadata.primary_role);
      const addRoles = user.user_metadata.additional_roles || [];
      setSelectedRoles([user.user_metadata.primary_role, ...addRoles]);
    }
  }, [profile, user, roles]);

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleToggleRole = (role) => {
    setError('');
    setSuccess('');

    if (selectedRoles.includes(role)) {
      if (selectedRoles.length === 1) {
        setError('You must have at least one active profession role.');
        return;
      }
      const updated = selectedRoles.filter((r) => r !== role);
      setSelectedRoles(updated);
      if (primaryRole === role) {
        setPrimaryRole(updated[0]);
      }
    } else {
      const updated = [...selectedRoles, role];
      setSelectedRoles(updated);
      if (!primaryRole) {
        setPrimaryRole(role);
      }
    }
  };

  const handleSetPrimaryRole = (role, e) => {
    e.stopPropagation();
    if (selectedRoles.includes(role)) {
      setPrimaryRole(role);
    }
  };

  // Avatar Image Selection & Upload Handler
  const handleAvatarFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset file input value to allow selecting same file again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setError('');
    setSuccess('');

    // Pre-validate file on client before network call
    const validation = validateAvatarFile(file);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    setUploadingAvatar(true);
    try {
      const result = await uploadAvatarImage(file);
      setAvatarUrl(result.avatarUrl);
      setAvatarTimestamp(Date.now());
      setAvatarError(false);

      if (refreshUserData) {
        await refreshUserData();
      }

      setSuccess('Profile picture updated successfully');
    } catch (err) {
      console.error('[EditProfilePage] Avatar upload error:', err);
      setError(err.message || 'Profile picture upload failed. Please try again.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Confirm Remove Avatar Handler
  const handleConfirmRemoveAvatar = async () => {
    setError('');
    setSuccess('');
    setRemovingAvatar(true);

    try {
      await removeAvatarImage();
      setAvatarUrl('');
      setAvatarError(false);
      setShowRemoveModal(false);

      if (refreshUserData) {
        await refreshUserData();
      }

      setSuccess('Profile picture removed successfully');
    } catch (err) {
      console.error('[EditProfilePage] Remove avatar error:', err);
      setError(err.message || 'Unable to remove your profile picture. Please try again.');
    } finally {
      setRemovingAvatar(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (!form.stage_name.trim()) {
      setError('Stage name is required.');
      return;
    }

    const trimmedAbout = (form.artist_web_about || '').trim();
    if (trimmedAbout.length > 150) {
      setError('About for Artist Web cannot exceed 150 characters.');
      return;
    }

    const rawInstagram = (form.instagram || '').trim();
    const normalizedInstagram = normalizeInstagramUsername(rawInstagram);

    if (rawInstagram && normalizedInstagram) {
      setSaving(true);
      const availability = await checkInstagramUsernameAvailable(supabase, normalizedInstagram, user.id);
      if (!availability.available) {
        setError('This Instagram username is already being used by another Artist Web profile.');
        setSaving(false);
        return;
      }
    }

    if (selectedRoles.length === 0) {
      setError('Please select at least one profession role.');
      return;
    }

    const cleanPrimaryGenre = (form.genre || '').trim();
    const cleanSecondaryGenre = (form.secondary_genre || '').trim();

    if (
      cleanPrimaryGenre &&
      cleanSecondaryGenre &&
      cleanPrimaryGenre.toLowerCase() === cleanSecondaryGenre.toLowerCase()
    ) {
      setError('Secondary genre cannot be identical to your primary genre.');
      return;
    }

    setSaving(true);
    try {
      // 1. Update basic profile info atomically
      await updateProfile({
        name: form.name.trim() || null,
        legal_name: form.legal_name.trim() || null,
        stage_name: form.stage_name.trim(),
        genre: cleanPrimaryGenre || null,
        secondary_genre: cleanSecondaryGenre || null,
        city: form.city.trim() || null,
        phone: form.phone.trim() || null,
        artist_web_about: trimmedAbout || null,
        spotify_link: form.spotify_link.trim() || null,
        apple_music_link: form.apple_music_link.trim() || null,
        youtube_music_link: form.youtube_music_link.trim() || null,
        instagram: normalizedInstagram || null,
        youtube: form.youtube.trim() || null,
        upi_id: form.upi_id.trim() || null,
        avatar_url: avatarUrl || null,
        onboarding_completed: true,
      });

      // 2. Update creator roles (updates permissions in real-time)
      await updateRoles(selectedRoles, primaryRole || selectedRoles[0]);

      // 3. Mark onboarding as complete in database
      await completeOnboarding();

      if (refreshUserData) {
        await refreshUserData();
      }

      const successMsg = isFirstTime ? 'Profile created successfully' : 'Profile edited successfully';
      setSuccess(successMsg);

      // Resolve redirect destination across query param, route state, and sessionStorage
      let targetDestination = searchParams.get('redirect');
      if (!targetDestination && location.state?.from) {
        if (typeof location.state.from === 'string') {
          targetDestination = location.state.from;
        } else if (location.state.from.pathname) {
          targetDestination = location.state.from.pathname + (location.state.from.search || '');
        }
      }
      if (!targetDestination) {
        try {
          const saved = sessionStorage.getItem('auth_redirect_destination');
          if (saved && saved !== '/login' && saved !== '/auth/callback' && !saved.startsWith('/edit-profile')) {
            targetDestination = saved;
          }
        } catch {
          // Ignore storage exceptions
        }
      }

      // Cleanup saved redirect once consumed
      try {
        sessionStorage.removeItem('auth_redirect_destination');
      } catch {
        // Ignore storage exceptions
      }

      // Intentional delay before redirecting so the user sees the confirmation
      setTimeout(() => {
        if (
          targetDestination &&
          targetDestination.startsWith('/') &&
          !targetDestination.startsWith('/edit-profile') &&
          targetDestination !== '/login' &&
          targetDestination !== '/auth/callback'
        ) {
          navigate(targetDestination, { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }, 1200);
    } catch (err) {
      console.error('[EditProfilePage] Save exception:', err);
      setError(err.message || 'Unable to save your profile. Please try again.');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-skeleton">
        <div className="auth-skeleton-card">
          <div className="auth-skeleton-line medium" />
          <div className="auth-skeleton-line short" />
          <div className="auth-skeleton-line tall" />
        </div>
      </div>
    );
  }

  const avatarInitial = (form.stage_name?.[0] || profile?.stage_name?.[0] || user?.email?.[0] || '?').toUpperCase();

  return (
    <div className="edit-profile-page">
      {/* Floating Success Popup Toast */}
      {success && (
        <div className="auth-success-toast" role="status" aria-live="polite">
          <CheckCircle2 size={20} color="#d9fb06" />
          <span>{success}</span>
        </div>
      )}

      {/* Remove Avatar Confirmation Modal */}
      {showRemoveModal && (
        <div className="avatar-modal-backdrop" role="dialog" aria-modal="true">
          <div className="avatar-modal-card">
            <h3 className="avatar-modal-title">Remove your profile picture?</h3>
            <p className="avatar-modal-text">
              This will restore your default avatar placeholder across your profile, dashboard, and Artist Web.
            </p>
            <div className="avatar-modal-actions">
              <button
                type="button"
                className="avatar-modal-btn avatar-modal-btn-cancel"
                onClick={() => setShowRemoveModal(false)}
                disabled={removingAvatar}
              >
                Cancel
              </button>
              <button
                type="button"
                className="avatar-modal-btn avatar-modal-btn-confirm"
                onClick={handleConfirmRemoveAvatar}
                disabled={removingAvatar}
              >
                {removingAvatar ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="edit-profile-container">
        {!isFirstTime && (
          <Link to="/dashboard" className="edit-profile-back" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
            <ArrowLeft size={14} /> Back to Dashboard
          </Link>
        )}

        <div className="edit-profile-card">
          {isFirstTime && (
            <div style={{ textAlign: 'center', marginBottom: '1.2rem' }}>
              <img src={logo} alt="SenritsuX" className="auth-brand-logo" style={{ maxHeight: '36px' }} />
            </div>
          )}

          <h1 className="edit-profile-title">
            {isFirstTime ? 'Complete Your Profile' : 'Edit Profile'}
          </h1>
          <p className="edit-profile-subtitle">
            {isFirstTime
              ? 'Set up your SenritsuX profile before entering your dashboard.'
              : 'Update your SenritsuX creator profile and profession roles.'}
          </p>

          {error && (
            <div className="auth-global-error">
              <AlertTriangle size={16} />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="auth-global-success" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.2rem' }}>
              <CheckCircle2 size={16} color="#d9fb06" />
              <span>{success}</span>
            </div>
          )}

          {/* ── PROFILE PICTURE SECTION ── */}
          <div className="edit-profile-avatar-section">
            <div className="edit-profile-avatar-frame">
              {avatarUrl && !avatarError ? (
                <img
                  src={`${avatarUrl}${avatarUrl.includes('?') ? '&' : '?'}t=${avatarTimestamp}`}
                  alt={form.stage_name || 'Profile picture'}
                  onError={() => setAvatarError(true)}
                />
              ) : (
                avatarInitial
              )}
            </div>

            <input
              type="file"
              ref={fileInputRef}
              accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
              onChange={handleAvatarFileChange}
              style={{ display: 'none' }}
              aria-label="Upload profile picture"
            />

            <div className="edit-profile-avatar-buttons">
              <button
                type="button"
                className="avatar-action-btn avatar-action-btn-primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar || saving || removingAvatar}
              >
                {uploadingAvatar ? (
                  <>
                    <span className="auth-spinner auth-spinner-light" style={{ width: 14, height: 14 }} /> Uploading...
                  </>
                ) : (
                  <>
                    <Camera size={15} /> Change Photo
                  </>
                )}
              </button>

              {avatarUrl && (
                <button
                  type="button"
                  className="avatar-action-btn avatar-action-btn-danger"
                  onClick={() => setShowRemoveModal(true)}
                  disabled={uploadingAvatar || saving || removingAvatar}
                >
                  <Trash2 size={15} /> Remove Photo
                </button>
              )}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.6rem' }}>
              Allowed formats: JPG, PNG, WEBP (Max 5 MB)
            </div>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Creator Roles Section */}
            <div className="edit-profile-section-title">Creator Roles &amp; Professions *</div>
            <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1rem' }}>
              Select all roles that apply to you. Your dashboard tools and access update automatically.
            </p>

            <div className="auth-profession-grid" style={{ marginBottom: '1.5rem' }}>
              {AVAILABLE_ROLES.map((role) => {
                const isSelected = selectedRoles.includes(role);
                const isPrimary = primaryRole === role;
                const RoleIconComp = ROLE_ICON_COMPONENTS[role] || User;

                return (
                  <button
                    key={role}
                    type="button"
                    className={`auth-profession-card ${isSelected ? 'selected' : ''} ${isPrimary ? 'primary' : ''}`}
                    onClick={() => handleToggleRole(role)}
                    style={{ position: 'relative' }}
                  >
                    <span className="auth-profession-emoji" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <RoleIconComp size={24} color={isSelected ? '#d9fb06' : 'rgba(255,255,255,0.6)'} />
                    </span>
                    <span className="auth-profession-name">{ROLE_LABELS[role]}</span>
                    {isSelected && (
                      <span
                        className="auth-profession-hint"
                        onClick={(e) => handleSetPrimaryRole(role, e)}
                        title="Click to set as primary role"
                        style={{ cursor: 'pointer' }}
                      >
                        {isPrimary ? '★ Primary' : 'Set Primary'}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="edit-profile-section-title">Basic Information</div>

            <div className="auth-input-row">
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="edit-name">Full Name</label>
                <input
                  id="edit-name"
                  type="text"
                  className="auth-input"
                  placeholder="Your full name"
                  value={form.name}
                  onChange={handleChange('name')}
                  disabled={saving}
                />
              </div>
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="edit-legalname">Legal Name (Real Name)</label>
                <input
                  id="edit-legalname"
                  type="text"
                  className="auth-input"
                  placeholder="Official legal name (for distribution & royalties)"
                  value={form.legal_name}
                  onChange={handleChange('legal_name')}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="auth-input-row">
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="edit-stagename">Stage Name / Artist Name *</label>
                <input
                  id="edit-stagename"
                  type="text"
                  className="auth-input"
                  placeholder="Your artist name"
                  value={form.stage_name}
                  onChange={handleChange('stage_name')}
                  disabled={saving}
                />
              </div>
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="edit-genre">Primary Genre</label>
                <input
                  id="edit-genre"
                  type="text"
                  className="auth-input"
                  placeholder="e.g. Hip-Hop / Rap, Pop, EDM"
                  value={form.genre}
                  onChange={handleChange('genre')}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="auth-input-row">
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="edit-secondary-genre">
                  Secondary Genre <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', fontWeight: 'normal' }}>(Optional)</span>
                </label>
                <input
                  id="edit-secondary-genre"
                  type="text"
                  className="auth-input"
                  placeholder="e.g. R&B / Soul, Indie, Lo-Fi"
                  value={form.secondary_genre}
                  onChange={handleChange('secondary_genre')}
                  disabled={saving}
                />
              </div>
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="edit-city">City</label>
                <input
                  id="edit-city"
                  type="text"
                  className="auth-input"
                  placeholder="e.g. Mumbai, Delhi, Bengaluru"
                  value={form.city}
                  onChange={handleChange('city')}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="auth-input-row">
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="edit-phone">Phone</label>
                <input
                  id="edit-phone"
                  type="tel"
                  className="auth-input"
                  placeholder="+91 XXXXX XXXXX"
                  value={form.phone}
                  onChange={handleChange('phone')}
                  disabled={saving}
                />
              </div>
            </div>

            {/* ── ABOUT FOR ARTIST WEB (MAX 150 CHARACTERS) ── */}
            <div className="auth-form-group" style={{ marginTop: '0.6rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                <label className="auth-label" htmlFor="edit-artist-web-about" style={{ marginBottom: 0 }}>
                  About for Artist Web
                </label>
                <span
                  style={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: (form.artist_web_about || '').length >= 150
                      ? '#ff6b6b'
                      : (form.artist_web_about || '').length >= 130
                      ? '#ffd166'
                      : 'rgba(255, 255, 255, 0.5)',
                  }}
                  aria-live="polite"
                >
                  {(form.artist_web_about || '').length}/150
                </span>
              </div>
              <textarea
                id="edit-artist-web-about"
                className="auth-input"
                rows={3}
                maxLength={150}
                placeholder="Short bio shown on your public Artist Web profile. Max 150 characters."
                value={form.artist_web_about}
                onChange={handleChange('artist_web_about')}
                disabled={saving}
                style={{ resize: 'vertical', minHeight: '76px', fontFamily: 'inherit' }}
              />
              <div style={{ fontSize: '0.78rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.35rem' }}>
                Short bio shown on your public Artist Web profile. Max 150 characters.
              </div>
            </div>

            <div className="edit-profile-section-title">Streaming &amp; Social Links</div>

            <div className="auth-form-group">
              <label className="auth-label" htmlFor="edit-spotify">Spotify Link</label>
              <input
                id="edit-spotify"
                type="url"
                className="auth-input"
                placeholder="https://open.spotify.com/artist/..."
                value={form.spotify_link}
                onChange={handleChange('spotify_link')}
                disabled={saving}
              />
            </div>

            <div className="auth-input-row">
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="edit-applemusic">Apple Music Link</label>
                <input
                  id="edit-applemusic"
                  type="url"
                  className="auth-input"
                  placeholder="https://music.apple.com/artist/..."
                  value={form.apple_music_link}
                  onChange={handleChange('apple_music_link')}
                  disabled={saving}
                />
              </div>
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="edit-ytmusic">YouTube Music Link</label>
                <input
                  id="edit-ytmusic"
                  type="url"
                  className="auth-input"
                  placeholder="https://music.youtube.com/..."
                  value={form.youtube_music_link}
                  onChange={handleChange('youtube_music_link')}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="auth-input-row">
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="edit-instagram">Instagram</label>
                <input
                  id="edit-instagram"
                  type="text"
                  className="auth-input"
                  placeholder="@username or profile URL"
                  value={form.instagram}
                  onChange={handleChange('instagram')}
                  disabled={saving}
                />
                <div style={{ fontSize: '0.78rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.35rem' }}>
                  {form.instagram && normalizeInstagramUsername(form.instagram)
                    ? `Public Artist Web URL: https://senritsux.in/artist/${normalizeInstagramUsername(form.instagram)}`
                    : 'Determines your unique public Artist Web address (e.g. senritsux.in/artist/yourusername).'}
                </div>
              </div>
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="edit-youtube">YouTube</label>
                <input
                  id="edit-youtube"
                  type="text"
                  className="auth-input"
                  placeholder="Channel URL or @handle"
                  value={form.youtube}
                  onChange={handleChange('youtube')}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="edit-profile-section-title">Payment</div>

            <div className="auth-form-group">
              <label className="auth-label" htmlFor="edit-upi">UPI ID</label>
              <input
                id="edit-upi"
                type="text"
                className="auth-input"
                placeholder="yourname@upi"
                value={form.upi_id}
                onChange={handleChange('upi_id')}
                disabled={saving}
              />
            </div>

            <button
              type="submit"
              className="auth-btn"
              disabled={saving || uploadingAvatar || removingAvatar}
              style={{ marginTop: '1.5rem', width: '100%' }}
            >
              {saving ? (
                <>
                  <span className="auth-spinner" /> Saving Profile...
                </>
              ) : (
                isFirstTime ? 'Save & Enter Dashboard' : 'Save Changes'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
